import "twin.macro";

import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";

import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import _ from "lodash";

import { OpaqueCard } from "@components/StyledComponents";
import { variantName } from "@features/items";
import IndexTable, { PaginatedResourceProps } from "@features/ui/indexTable";
import { insertIf } from "@features/ui/indexTable/helpers";
import { PurchaseOrder } from "@models";
import { PurchaseOrderVariantItem } from "@models/PurchaseOrderVariant";
import { formatDateString, formatMoneyString } from "@utility/utilityFunctions";

import { purchaseOrdersKeyFactory } from "../data";
import { poTypeLabel } from "../helpers";

type POVTableProps = PaginatedResourceProps & {
  rows: PurchaseOrderVariantItem[];
};

const PurchaseOrderHistoryVariantTable = ({
  rows,
  ...tableProps
}: POVTableProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPurchasingAgents, hasMultipleBrands, hasBeacons } = useSelector(
    (state: any) => state.currentUser.organization
  );
  return (
    <OpaqueCard tw="p-0">
      <IndexTable
        rows={rows}
        columns={[
          {
            id: "purchaseOrder.id",
            label: "PO #",
            sort: "id",
            reverseSort: true,
            render: (id, pov) => (
              <Link
                to={`/purchase-orders/history/${id}`}
                tw="font-medium underline"
                onClick={(e) => {
                  e.stopPropagation();
                  queryClient.setQueryData<PurchaseOrder>(
                    purchaseOrdersKeyFactory.detail(id).queryKey,
                    pov.purchaseOrder
                  );
                }}
              >
                {id}
              </Link>
            ),
          },
          {
            id: "purchaseOrder.type",
            label: "PO Type",
            sort: "type",
            render: (v) => <span tw="whitespace-nowrap">{poTypeLabel(v)}</span>,
          },
          {
            id: "purchaseOrder.supplier.name",
            label: "Supplier",
            sort: "supplier-name",
            render: (v) => <span tw="whitespace-nowrap">{v}</span>,
          },
          {
            id: "purchaseOrder.supplierReference",
            label: "Supplier Reference",
          },
          { id: "purchaseOrder.invoiceNumber", label: "Invoice #" },
          ...insertIf(hasPurchasingAgents, {
            id: "purchaseOrder.purchasingAgentReferenceNumber",
            label: "External PO #",
          }),
          {
            id: "purchaseOrder.purchaserName",
            label: "Purchaser",
            sort: "purchaser-name",
          },
          {
            id: "programNames",
            label: "Program",
            render: (p) => <div tw="min-w-40 line-clamp-2">{p}</div>,
          },
          {
            id: "variant.variantSku",
            label: "SKU",
            className: "whitespace-nowrap",
            render: (variantSku, pov) =>
              pov.isSetupFee
                ? "Setup Fee"
                : (pov.variant.externalWarehouseId ?? variantSku),
          },
          {
            id: "variant.item.name",
            label: "Item Name",
            render: (itemName, pov) => (
              <div tw="min-w-64">
                {itemName}
                <div tw="text-xs text-neutral-500">
                  {variantName(pov.variant)}
                </div>
              </div>
            ),
          },
          ...insertIf(hasMultipleBrands, {
            id: "variant.item.brands",
            label: "Brand",
            render: (brands) => brands.map((b) => b.name).join(", "),
          }),
          {
            id: "qty",
            label: "Quantity",
          },
          {
            id: "actualCost",
            label: "Act. Cost/Unit",
            render: formatMoneyString,
          },
          {
            id: "_totalCost",
            label: "Total Item Cost",
            render: (_, pov) => formatMoneyString(+pov.actualCost * pov.qty),
          },
          {
            id: "purchaseOrder.status",
            label: "Status",
            sort: "status",
            render: (v) => <span tw="whitespace-nowrap">{_.startCase(v)}</span>,
          },
          {
            id: "purchaseOrder.submittedAt",
            label: "Submitted Date",
            sort: "submitted-at",
            reverseSort: true, // po-submitted-at
            render: (d) => d && format(new Date(d), "MM/dd/yyyy"),
          },
          {
            id: "purchaseOrder.inMarketDate",
            label: "In-Market Date",
            sort: "in-market-date",
            reverseSort: true,
            render: formatDateString,
          },
          ...insertIf(hasBeacons, {
            id: "hasBeacons",
            label: "Has Beacons",
            render: (v) => (v ? "Yes" : "No"),
          }),
          {
            id: "shipmentCount",
            label: "Shipment Count",
          },
        ]}
        onRowClick={(row) => {
          queryClient.setQueryData<PurchaseOrder>(
            purchaseOrdersKeyFactory.detail(row.purchaseOrder.id).queryKey,
            row.purchaseOrder
          );
          navigate(`/purchase-orders/history/${row.purchaseOrder.id}`);
        }}
        {...tableProps}
      />
    </OpaqueCard>
  );
};

export default PurchaseOrderHistoryVariantTable;
